import { useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { CategoriesGroup } from 'types/Category';
import Button from 'components/UI/Button';
import { HookForm } from 'components/UI/HookForm/Form';
import { HookInput } from 'components/UI/HookForm/Form/Input';
import { HookReactSingleSelect } from 'components/UI/HookForm/Form/ReactSelect';
import { HookTextarea } from 'components/UI/HookForm/Form/TextArea';
import { HookFormProvider } from 'components/UI/HookForm/Provider';
import {
  useCategoryGroup,
  useCategorySubGroup,
} from 'hooks/ReactQueryHooks/useCategoryQuery';
import Modal from 'components/UI/Modal';
import { createCategory, CreateCategoryPayload } from 'api/category';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { notifyUser } from 'store/Toast';
import { categoryValidationSchema } from './validationSchema';
import { sanitizeResponseError } from 'utils';
import { ReactQueryKey } from 'constants/ReactQueryKeys';

const NewCategoryBaseForm = (props: NewCategoryFormProps) => {
  const { handleModal } = props;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const groupValues = useWatch({ name: 'categoryGroup' });

  const fetchCategoriesGroup = useCategoryGroup({});
  const categoriesGroup = fetchCategoriesGroup?.data?.data;

  const fetchCategoriesSubGroup = useCategorySubGroup({ group: groupValues });
  const categoriesSubGroup = fetchCategoriesSubGroup?.data?.data;

  const postNewCategory = useMutation(
    (payload: CreateCategoryPayload) => createCategory(payload),
    {
      onSuccess: () => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Category added successfully`,
            toastType: 'success',
          })
        );
        handleModal();
        queryClient.invalidateQueries([ReactQueryKey.GET_CATEGORIES]);
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const handleSubmit = (formData: any) => {
    let categoryTypeId = formData.categoryGroup.id;
    if (formData.categorySubGroup.id !== undefined) {
      categoryTypeId = formData.categorySubGroup.id;
    }
    const newCategoryData: CreateCategoryPayload = {
      name: formData.name,
      description: formData.description,
      categoryTypeId,
    };
    postNewCategory.mutate(newCategoryData);
  };

  return (
    <HookForm onSubmit={handleSubmit}>
      <h2 className='m-0 pb-5'>Add New Category</h2>
      <section className='space-y-5'>
        <HookInput
          type='text'
          label='Category Name'
          name='name'
          id='categoryName'
          placeholder='e.g. Furniture'
          required
        />
        <HookReactSingleSelect
          id='categoryGroup'
          name='categoryGroup'
          label='Category Group'
          required
          options={categoriesGroup}
          getOptionLabel={(option) => (option as CategoriesGroup).name}
          getOptionValue={(option) => (option as CategoriesGroup).id.toString()}
        />
        <HookReactSingleSelect
          id='categorySubGroup'
          name='categorySubGroup'
          label='Category Sub Group (Optional)'
          isDisabled={!groupValues}
          options={categoriesSubGroup}
          getOptionLabel={(option) => (option as CategoriesGroup).name}
          getOptionValue={(option) => (option as CategoriesGroup).id.toString()}
        />
        <HookTextarea name='description' label='Description (Optional)' />
      </section>
      <div className='flex gap-2 justify-end mt-5'>
        <Button
          label='Cancel'
          btnStyle='btn-neutral'
          type='button'
          onClick={handleModal}
        />

        <Button label='Add' btnStyle='btn-primary' type='submit' />
      </div>
    </HookForm>
  );
};

interface NewCategoryFormProps {
  handleModal: () => void;
}

export const NewCategoryForm = (props: NewCategoryFormProps) => {
  return (
    <Modal>
      <HookFormProvider
        resolver={yupResolver(categoryValidationSchema)}
        mode='all'
      >
        <NewCategoryBaseForm {...props} />
      </HookFormProvider>
    </Modal>
  );
};
